/**
 * @fileoverview Redirects users to the appropriate language version of the landing page.
 * @module features/landing/components/LandingRedirect
 */

import { ReactElement, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { detectBrowserLanguage } from '@/shared/components/common/seoUtils'
import { authService } from '@/lib/authService'
import { getPostAuthRedirect } from '@/lib/pendingInvite'
import { supabase } from '@/lib/supabaseClient'

/**
 * Returns true when the current URL contains Supabase auth callback parameters
 * (hash fragment with access_token or query string with code).
 */
function hasAuthCallbackParams(): boolean {
  const hash = window.location.hash
  const search = window.location.search
  return hash.includes('access_token') || new URLSearchParams(search).has('code')
}

/**
 * Redirects to the appropriate language landing page based on browser settings.
 * Authenticated users are redirected to the dashboard (or pending invite) instead.
 *
 * When arriving from Supabase email verification, waits for the session to be
 * established before redirecting (the Supabase JS client needs a moment to
 * process auth tokens from the URL fragment).
 */
export function LandingRedirect(): ReactElement {
  const [waiting, setWaiting] = useState(hasAuthCallbackParams)

  useEffect(() => {
    if (!waiting) return

    // Listen for Supabase to establish the session from URL tokens
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        window.location.replace(getPostAuthRedirect())
      }
    })

    // Safety timeout: if Supabase doesn't fire within 5s, stop waiting
    const timeout = setTimeout(() => setWaiting(false), 5000)

    return () => {
      data.subscription.unsubscribe()
      clearTimeout(timeout)
    }
  }, [waiting])

  if (authService.isAuthenticated()) {
    window.location.replace(getPostAuthRedirect())
    return <></>
  }

  if (waiting) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="text-sm text-muted-foreground">Signing you in…</div>
      </div>
    )
  }

  const lang = detectBrowserLanguage()
  return <Navigate to={`/${lang}/`} replace />
}

/**
 * Wraps a landing page element so that authenticated users are redirected to the dashboard.
 * Uses a full-page redirect (not React Router Navigate) because landing→dashboard is a
 * cross-context switch that requires clean provider initialization (Supabase auth, ActiveOrg).
 */
export function RequireGuest({ children }: { children: ReactElement }): ReactElement {
  if (authService.isAuthenticated()) {
    window.location.replace(getPostAuthRedirect())
    return <></>
  }
  return children
}
